import { Loader2 } from 'lucide-react'
import { AiPromptIcon } from '../../shared/AiPromptIcon'
import { FailureBanner } from '@/features/ai/components/FailureBanner'
import { MenuButton } from './MenuButton'
import { formatCredits, formatCreditsWithWord, hasEnough } from '@/features/ai/lib/money'
import { CreditIcon } from '@/components/common/CreditIcon'
import { BalanceShortNotice } from './BalanceShortNotice'
import { DisabledHint } from './DisabledHint'
import { TranslatePlan } from './TranslatePlan'
import { buttonPrice } from './translatePrice'
import { TranslationScope } from './TranslationScope'
import type { TranslationLauncher } from './useTranslationLauncher'
import { useMessages } from '@/i18n'
import { messages } from './messages'

type Ask = (d: 'refresh' | 'overwrite') => void

interface Props {
  launcher: TranslationLauncher
  onAsk: Ask
}

/**
 * Повтор старта после отказа — тем же режимом, каким он упал: у
 * разрушительного другая цена и другое подтверждение, и «повторить» мягким
 * там означало бы другой прогон. Разрушительный повтор проходит через то же
 * подтверждение с ценой — он заменяет и ручные правки.
 */
const retryOf = (
  launcher: TranslationLauncher,
  onStart: () => void,
  onAsk: Ask,
): (() => void) | undefined => {
  if (launcher.lastOverwrite) return launcher.canHard ? () => onAsk('overwrite') : undefined
  return launcher.canSoft ? onStart : undefined
}

/** Отказ печатает ОБЩИЙ баннер: вместе с текстом приезжает кнопка пополнения,
 *  иначе перевод остался бы единственным платным стартом без выхода из «не
 *  хватает AI-кредитов». */
const Notices = ({
  launcher,
  onRetry,
}: {
  launcher: TranslationLauncher
  onRetry?: () => void
}) => {
  const m = useMessages(messages)
  return (
    <>
      {launcher.isFrozen ? <p className="text-xs text-warning">{m.underReview}</p> : null}
      {launcher.failure ? (
        <FailureBanner
          failure={launcher.failure}
          onRetry={onRetry}
          reserveReturning={launcher.reserveReturning}
        />
      ) : null}
    </>
  )
}

/**
 * Панель запуска перевода в меню языка шапки (спека §05): объём, цена и ОДНА
 * кнопка «Перевести» (решение заказчика: «Перевести заново» убрана). Перезапись
 * ручных правок (`overwrite`) отсюда больше не запускается. Логика — `useTranslationLauncher`, диалоги —
 * `TranslationDialogs` рядом с меню: закрытие меню размонтирует эту панель.
 *
 * Всё, чем автор управляет, — пункты меню (`TranslationScope`, `MenuButton`):
 * до обычных кнопок внутри `DropdownMenuContent` клавиатура не доходит.
 */
export const TranslationPanel = ({ launcher, onAsk }: Props) => {
  const m = useMessages(messages)
  const { quote } = launcher
  // База правилась после перевода — та же ручка, но перезапись ручных правок
  // в устаревших местах требует подтверждения.
  // Цена — прямо на кнопке: сумма с картинками, если они в объёме, иначе только текст.
  // Недоехавшая цена не печатается нулём (`priceToShow`) — ноль обещал бы бесплатный прогон.
  const price = buttonPrice(quote, launcher.showsImages)
  // Не хватает баланса — гасим запуск заранее, а не ждём отказа сервера.
  const short = price !== null && quote.balance !== undefined && !hasEnough(quote.balance, price)
  const onStart = () => (quote.hasOutdated ? onAsk('refresh') : launcher.run(false))
  return (
    <div className="space-y-2.5 p-2">
      {/* Цена — по тому, что на экране; тело старта — по тому же выражению,
          кроме случая «картинок нет вовсе» (правило `willTranslateImages`). */}
      <TranslatePlan quote={quote} quoteFailed={launcher.quoteFailed} />
      <DisabledHint text={quote.pendingUnits === 0 ? m.nothingToTranslate : null}>
        <MenuButton disabled={!launcher.canSoft || short} onSelect={onStart}>
          {launcher.isStarting ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <AiPromptIcon className="size-5" />
          )}
          {quote.hasOutdated ? m.refreshTranslation : m.translate}
          {price ? (
            <span
              aria-label={formatCreditsWithWord(price)}
              className="ml-2 flex items-center gap-1 rounded-full bg-[hsl(var(--credit)/0.15)] px-2 py-0.5 text-xs font-semibold text-[hsl(var(--credit))]"
            >
              <CreditIcon className="size-3.5" />
              {formatCredits(price)}
            </span>
          ) : null}
        </MenuButton>
      </DisabledHint>
      {short && quote.balance !== undefined && price !== null ? (
        <BalanceShortNotice balance={quote.balance} required={price} />
      ) : null}
      <TranslationScope launcher={launcher} />
      <Notices launcher={launcher} onRetry={retryOf(launcher, onStart, onAsk)} />
    </div>
  )
}
